"use client";

//region Imports
import React, {useRef, useState, useEffect} from 'react';
import {Application, type SplineEvent} from '@splinetool/runtime';
import _ from 'lodash';

import Spline from './Spline3JS';
//endregion Imports

//region Types & Interfaces
interface SplineObjectProps {
  windowState: any;
  splineSettings: any;
  links: string[];
  onLoadComplete: (app: any) => void;
  onSplineScroll: (e: SplineEvent) => void;
  onSplineClick: (e: any) => void;
}
//endregion Types & Interfaces

export const SplineObject: React.FC<SplineObjectProps> = ({
                                                            windowState,
                                                            splineSettings,
                                                            links,
                                                            onLoadComplete,
                                                            onSplineClick,
                                                            onSplineScroll
                                                          }) => {
  //region Variables
  const spline = useRef<Application | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [splineLoaded, setSplineLoaded] = useState(false);
  const isHovering = useRef(false);
  //endregion Variables

  //region Methods & Event Handlers
  const onHover = (e: SplineEvent) => {
    if (links.includes(e?.target?.name)) {
      isHovering.current = true;
      document.body.style.cursor = 'pointer';
    }
  };

  const onPointerMove = () => {
    if (!isHovering.current) {
      document.body.style.cursor = 'grab';
    }
    isHovering.current = false;
  };

  const onClick = (e: SplineEvent) => {
    document.body.style.cursor = 'default';
    onSplineClick?.(e);
  };

  const onScroll = (e: any) => {
    if (!isHovering.current) {
      document.body.style.cursor = 'grab';
    }
    onSplineScroll?.(e);
  };

  const onLoad = (app: Application, _canvasRef: React.RefObject<HTMLCanvasElement>) => {
    if (!app) return;

    spline.current = app;
    if (_.isBrowser()) {
      (window as any).$spline = app;
    }

    spline.current.addEventListener("mouseHover", onHover);
    spline.current.addEventListener("mouseDown", onClick);

    canvasRef.current = _canvasRef.current;
    if (canvasRef?.current) {
      canvasRef.current.setAttribute('tabindex', '0');
      canvasRef.current.addEventListener('pointermove', onPointerMove);
      canvasRef.current.addEventListener('wheel', onScroll, {passive: false});
    }

    setSplineLoaded(true);
    onLoadComplete?.(app);
  }
  //endregion Methods & Event Handlers

  //region Effects
  useEffect(() => {
    return () => {
      document.body.style.cursor = 'default';

      if (spline.current) {
        spline.current.removeEventListener("scroll", onScroll);
        spline.current.removeEventListener("mouseHover", onHover);
        spline.current.removeEventListener("mouseDown", onClick);
      }

      if (canvasRef?.current) {
        canvasRef.current.removeEventListener('pointermove', onPointerMove);
        canvasRef.current.removeEventListener('wheel', onScroll);
      }
    };
  }, []);
  //endregion Effects

  return (
    <div
      style={{
        opacity: splineLoaded ? 1 : 0,
        transition: 'opacity 40ms ease',
        pointerEvents: splineLoaded ? 'auto' : 'none'
      }}
    >
      <Spline
        windowState={windowState}
        settings={splineSettings}
        onLoad={onLoad}
      />
    </div>
  );
};

export default SplineObject;